import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Pagination,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ErrorOutline } from "@mui/icons-material";
import axios from "axios";
import CallActivityGraph from "../reusables/CallActivotyGraph";
import RecentCalls from "./RecentCalls";

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);
  const [calls, setCalls] = useState([]);
  const [dateRange, setDateRange] = useState(30); // Default: Last 30 days
  const [frequency, setFrequency] = useState("daily"); // Default: Daily
  const [callDateRange, setCallDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const isFetchingRef = useRef(false);
  const lastRequestParamsRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const lastLocationRef = useRef(location.pathname);
  
  // Helper function to format calls from API response
  const formatCall = (call) => ({
    callTime: call.startTime?.N || call.startTime || "N/A",
    toNumber: call.toNumber?.S || call.toNumber || "N/A",
    callerNumber: call.fromNumber?.S || call.fromNumber || "N/A",
    action: call.action?.S || call.action || "N/A",
    actionDetails: call.action_details?.S || call.actionDetails || "N/A",
    voicemail: call.has_vm?.BOOL ? "Yes" : (call.voicemail === "Yes" ? "Yes" : "No"),
    link: "/dashboard/call-details/" + (call.callId?.S || call.callId),
    callId: call.callId?.S || call.callId,
    call_user: call.call_user?.S || call.call_user || "",
    route: call.route?.S || call.route || ""
  });

  const getCalls = async (startDate = null, endDate = null) => {
    // Validate date range: start date must be before or equal to end date
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setLoader(false);
      isFetchingRef.current = false;
      return; // Don't make API call
    }
    
    // Create a unique key for this request
    const requestKey = `${startDate || 'default'}_${endDate || 'default'}_${dateRange}`;
    
    // Prevent duplicate calls with same parameters
    if (isFetchingRef.current && lastRequestParamsRef.current === requestKey) {
      return;
    }
    
    // Prevent any duplicate calls while fetching
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    lastRequestParamsRef.current = requestKey;
    setLoader(true);
    try {
      let startTime, endTime;
      
      if (startDate) {
        // Get organization timezone
        const orgData = JSON.parse(localStorage.getItem("organization"));
        const orgTimezone = orgData?.org_timezone?.S || Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Helper function to get Unix timestamp for a specific date/time in org timezone
        const getTimestampInTimezone = (dateStr, hour, minute, second) => {
          // Create a date string for the desired time
          const dateTimeStr = `${dateStr}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
          
          // Use a simple approach: create date and adjust until it matches in org timezone
          const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: orgTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });
          
          // Start with a reasonable guess (assume dateStr is already in org timezone date)
          let testDate = new Date(dateTimeStr);
          let attempts = 0;
          const maxAttempts = 5;
          
          while (attempts < maxAttempts) {
            const parts = formatter.formatToParts(testDate);
            const testYear = parts.find(p => p.type === 'year').value;
            const testMonth = parts.find(p => p.type === 'month').value;
            const testDay = parts.find(p => p.type === 'day').value;
            const testHour = parts.find(p => p.type === 'hour').value;
            const testMinute = parts.find(p => p.type === 'minute').value;
            const testSecond = parts.find(p => p.type === 'second').value;
            
            const [targetYear, targetMonth, targetDay] = dateStr.split('-');
            
            if (testYear === targetYear && 
                testMonth === targetMonth && 
                testDay === targetDay &&
                testHour === String(hour).padStart(2, '0') &&
                testMinute === String(minute).padStart(2, '0') &&
                testSecond === String(second).padStart(2, '0')) {
              return Math.floor(testDate.getTime() / 1000);
            }
            
            // Calculate difference and adjust
            const hourDiff = hour - parseInt(testHour);
            const minuteDiff = minute - parseInt(testMinute);
            const secondDiff = second - parseInt(testSecond);
            const totalSecondsDiff = hourDiff * 3600 + minuteDiff * 60 + secondDiff;
            
            testDate = new Date(testDate.getTime() + totalSecondsDiff * 1000);
            attempts++;
          }
          
          // Fallback: return the test date even if not perfect
          return Math.floor(testDate.getTime() / 1000);
        };
        
        const startDateStr = startDate.split('T')[0]; // Get YYYY-MM-DD part
        startTime = getTimestampInTimezone(startDateStr, 0, 0, 0);
        
        if (endDate) {
          const endDateStr = endDate.split('T')[0]; // Get YYYY-MM-DD part
          endTime = getTimestampInTimezone(endDateStr, 23, 59, 59);
        } else {
          // No end date provided - use current time (fetch up to now)
          endTime = Math.floor(Date.now() / 1000);
        }
      } else {
        // Calculate time range based on dateRange (in days) - for chart
        const now = Date.now();
        startTime = Math.floor((now - dateRange * 24 * 60 * 60 * 1000) / 1000); // Convert to seconds
        endTime = Math.floor(now / 1000); // Convert to seconds
      }

      // Get organization ID
      const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
      
      // Direct API call to get calls
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "call/get-calls",
        {
          organization_id: orgId,
          start_time: startTime,
          end_time: endTime,
        }
      );
      
      const calls = response.data.calls || [];
      const formattedCalls = calls.map(formatCall);
      
      if (formattedCalls && formattedCalls.length > 0) {
        setCalls(formattedCalls);
      } else {
        setCalls([]);
      }

      setLoader(false);
      isFetchingRef.current = false;
    } catch (error) {
      const status = error?.response?.status;
      const apiError = error?.response?.data?.error;
      const apiMessage = error?.response?.data?.message;

      // If the API says auth header/token is missing/invalid, show a user-friendly prompt.
      if (
        status === 401 &&
        typeof apiError === "string" &&
        apiError.toLowerCase().includes("authorization header")
      ) {
        setErrorMsg(
          "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
        );
      } else if (error.response && error.response.data) {
        setErrorMsg([apiError, apiMessage].filter(Boolean).join(" "));
      }
      setLoader(false);
      isFetchingRef.current = false;
    }
  };

  // Calculate date range based on dateRange (for chart)
  const calculateDateRange = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Handle date range changes from RecentCalls component
  const handleDateRangeChange = (startDate, endDate) => {
    // Validate date range: start date must be before or equal to end date
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return; // Don't make API call
    }
    
    setCallDateRange({ startDate, endDate });
    getCalls(startDate, endDate);
  };

  // Track if we've loaded data in this render cycle (prevents StrictMode double calls)
  const hasLoadedInThisCycleRef = useRef(false);

  // Initial load - use default 30 days (same as dateRange default)
  // Always load data when component mounts or when location changes to dashboard
  useEffect(() => {
    // Prevent double calls from React StrictMode
    if (hasLoadedInThisCycleRef.current) {
      return;
    }
    
    const { startDate, endDate } = calculateDateRange(dateRange);
    setCallDateRange({ startDate, endDate });
    getCalls(startDate, endDate);
    hasLoadedInThisCycleRef.current = true;
    isInitialMountRef.current = false;
    
    // Reset flag after a short delay to allow for StrictMode double render
    const timeoutId = setTimeout(() => {
      hasLoadedInThisCycleRef.current = false;
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      // Reset when component unmounts so it loads again when navigating back
      hasLoadedInThisCycleRef.current = false;
      isInitialMountRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // When dateRange dropdown changes, update both chart and call list
  // Skip on initial mount (handled by the useEffect above)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      return;
    }

    // Calculate dates based on dropdown selection
    const { startDate, endDate } = calculateDateRange(dateRange);
    
    // Always update callDateRange and fetch calls when dropdown changes
    // This keeps both chart and call list in sync with the dropdown
    setCallDateRange({ startDate, endDate });
    getCalls(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  return (
    <div className="pt-4 px-4">
      <div className="lg:flex items-center justify-between">
        <div className="lg:text-[22px] text-[20px] font-bold mb-4 lg:mt-0 mt-8 ">
          Dashboard
        </div>

        <Box display="flex" alignItems={"center"} gap={2} marginBottom={2}>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            displayEmpty
            sx={{
              fontSize: "14px",
              padding: "4px 8px", // Adjust padding here
              bgcolor: "white",
              color: "black",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "0.6px solid white",
              },
              "& .MuiSelect-select": {
                padding: "4px 8px", // Adjust padding for the text inside Select
              },
              width: { xs: "100%", lg: "fit" },
            }}
          >
            <MenuItem value={1}>Last 24 Hours</MenuItem>
            <MenuItem value={7}>Last 7 Days</MenuItem>
            <MenuItem value={30}>Last 30 Days</MenuItem>
            <MenuItem value={60}>Last 60 Days</MenuItem>
            <MenuItem value={90}>Last 90 Days</MenuItem>
          </Select>
          <Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            displayEmpty
            sx={{
              fontSize: "14px",
              padding: "4px 8px", // Adjust padding here
              bgcolor: "white",
              color: "black",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "0.6px solid white",
              },
              "& .MuiSelect-select": {
                padding: "4px 8px", // Adjust padding for the text inside Select
              },
              width: { xs: "100%", lg: "fit" },
            }}
          >
            <MenuItem value="hourly">Hourly</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
          </Select>
        </Box>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 relative h-[400px] mb-4 p-4">
        <CallActivityGraph
          call_data={calls}
          dateRange={dateRange}
          frequency={frequency}
          isLoading={loader}
          startDate={callDateRange.startDate}
          endDate={callDateRange.endDate}
        />
      </div>

      {err_msg && (
        <div className="text-center text-red-500 lg:text-[13px] text-[12px] mt-2 font-semibold mb-3">
          <ErrorOutline sx={{ fontSize: "" }} /> {err_msg}
        </div>
      )}

      {loader && (
        <CircularProgress
          size={"1.2rem"}
          sx={{ color: "#605BFF", animationDuration: "0.7s" }}
        />
      )}

      <div className="bg-white rounded-[13px] w-full relative">
        {calls && calls.length > 0 ? (
          <>
            <RecentCalls 
              navigate={navigate} 
              calls={calls} 
              onDateRangeChange={handleDateRangeChange}
              initialStartDate={callDateRange.startDate}
              initialEndDate={callDateRange.endDate}
            />
          </>
        ) : (
          <div className="p-4 text-[14px]">
            <div className="cursor-pointer text-[16px] font-semibold text-[#030229] mb-3 ">
              Recent Calls
            </div>
            You don't have any calls yet. In the future, this page will help you
            track your call volume.
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
